import _ from 'lodash';
import { Fragment, useState } from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useKeyboardNavigation } from '@treams/treams-hooks';
import { SearchIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Highlight, Input, KeyBinding, Lazy, Separator, tokens } from '@treams/treams-ui';

const Result = styled(Flex)<{ first?: boolean; selected?: boolean }>`
	cursor: pointer;

	${(props) => !props.first && `border-top: 1px solid ${tokens.surface.secondary};`}
	${(props) => props.selected && `background: ${tokens.surface.secondary};`}
`;

export default function GroupSelector(props: {
	placeholder?: string;
	onChange: (user: graphql.Group) => void;
}) {
	const [query, setQuery] = useState('');

	const { data, loading } = graphql.useGroupsQuery({
		variables: { first: 1000, filter: { isArchived: false } },
	});
	const groups = (data?.groups.edges.map(({ node }) => node) ?? []).filter((group) =>
		_.trim(group.name).toLowerCase().includes(_.trim(query).toLowerCase()),
	);

	const { onKeyDown, selectedIndex, setSelectedIndex } = useKeyboardNavigation({
		count: groups.length,
		key: query,
		onEnter: (index) => {
			props.onChange(groups[index] as graphql.Group);
		},
	});

	return (
		<Box width={300}>
			<Box p={2}>
				<Input
					placeholder={props.placeholder}
					onChange={setQuery}
					onKeyDown={onKeyDown}
					value={query}
					autoFocus
					small
					icon={<SearchIcon />}
					loading={loading}
				/>
			</Box>
			<Separator />
			<Box overflowY="auto" maxHeight={200}>
				{_.chunk(groups, 10).map((results, page) => (
					<Lazy key={page} estimatedHeight={results.length * 33 - 1}>
						{results.map((result, i) => (
							<Fragment key={result.id}>
								<Result
									role="option"
									aria-label={result.name}
									p={1}
									alignItems="center"
									first={i === 0 && page === 0}
									selected={page * 10 + i === selectedIndex}
									onMouseEnter={() => setSelectedIndex(page * 10 + i)}
									onClick={() => props.onChange(result as graphql.Group)}
								>
									<Box ml={2} flexGrow={1} flexShrink={1} lineHeight="24px">
										<Highlight query={query}>{result.name}</Highlight>
									</Box>
									{page * 10 + i === selectedIndex && <KeyBinding>Enter ⏎</KeyBinding>}
								</Result>
							</Fragment>
						))}
					</Lazy>
				))}
			</Box>
		</Box>
	);
}
